// src/prototype/roi/roiEngine.ts
// ============================================================================
// THE ROI FINANCIAL ENGINE — one model, pure, no imports, unit-tested.
//
// WHY THIS EXISTS. Every financial figure on the ROI Model page, in the Execute
// Command Center, and in the four approval PDFs used to be an independently
// authored literal. Five surfaces carried five confidence numbers for the same
// initiative (85% / 75% / 84% / 61% / "High"), two carried contradictory
// investment figures ($1.2M vs $2.1M–$3.4M), the "2 Modified" badge was a
// constant, and editing an assumption moved nothing. This module is the single
// place those numbers are computed; every surface reads its outputs.
//
// WHAT IT IS NOT. It is not a forecast. It is arithmetic over stated inputs,
// and its whole value is that the arithmetic is checkable by hand from the
// inputs and the four definitions below. Where an input is an estimate rather
// than a benchmark, that shows up in the confidence output — not in a hedge.
//
// ── THE FOUR DEFINITIONS (these are what the on-screen disclosure states) ───
//
// 1. ANNUAL VALUE. Drivers combine into one midpoint:
//        annualValueMid = Π(valueMultiplier drivers) + Σ(valueAddend drivers)
//    The published range is that midpoint ± `valueRangeSpread` (modelling
//    uncertainty), NOT a re-roll of the drivers — the driver bands are what the
//    scenarios and the sensitivity analysis are for.
//
// 2. ADOPTION RAMP. Value accrues linearly from zero to the full run-rate over
//    `rampMonths`, then holds. Year-1 realized value is the 12-month mean of
//    that curve × the annual midpoint. Years 2+ run at full rate.
//
// 3. PAYBACK. The first month where cumulative ramped value ≥ cumulative cost,
//    where cost = the implementation spend at month 0 plus operating cost
//    accrued monthly. Undiscounted, because payback is a cash question.
//
// 4. NET 3-YEAR VALUE. Benefits are discounted at `discountRate`; cost is the
//    nominal total 3-year cost (implementation + operating × 3), which is the
//    figure the PDFs state. Then:
//        netValue3Yr    = discountedValue3Yr − totalCost3Yr
//        roi3YrPct      = netValue3Yr / totalCost3Yr × 100
//        returnMultiple = discountedValue3Yr / totalCost3Yr  (= roi3YrPct/100 + 1)
//    One cost basis, both sides, so a reader can check it against the stated
//    total cost with a calculator.
//
// 5. THE DISPLAYED ROI% is a YEAR-ONE figure, and a different one from (4):
//        year1RoiPct = (year1RealizedValue − year1Cost) / year1Cost × 100
//    i.e. net value ÷ total year-1 cost, ramp-adjusted. Both periods on both
//    sides of the ratio — a 3-year numerator over a 1-year denominator would
//    inflate the number by construction, which is why that combination is not
//    offered here. This is the figure the ROI screen labels "ROI (Year 1)" and
//    captions; `roi3YrPct` remains available for the 3-year statement and is
//    stated alongside it in the disclosure. Two periods, two labels, never one
//    unlabelled percentage.
//
// CONFIDENCE is derived, not asserted — see `computeConfidence`.
// ============================================================================

export type ConfidenceLevel = 'High' | 'Medium' | 'Low';
export type SourceType = 'External Benchmark' | 'Historical' | 'Estimate';
export type ScenarioId = 'conservative' | 'base' | 'aggressive' | 'custom';

/** The named scenarios, in monotonic order. `custom` is excluded — it is derived. */
export const NAMED_SCENARIOS: readonly Exclude<ScenarioId, 'custom'>[] = [
  'conservative',
  'base',
  'aggressive',
] as const;

/**
 * How a driver enters the model. Exactly one role per driver, so there is never
 * an ambiguous double-count.
 *
 * - `valueMultiplier` — multiplied into the annual-value chain (volume, rate, unit cost)
 * - `valueAddend`     — added to the annual-value chain (a separate revenue stream)
 * - `ramp`            — months to full run-rate
 * - `implementationCost` — one-time, incurred at month 0
 * - `operatingCost`      — per year, accrued monthly
 */
export type DriverRole =
  | 'valueMultiplier'
  | 'valueAddend'
  | 'ramp'
  | 'implementationCost'
  | 'operatingCost';

/**
 * One model input. Values are held in DISPLAY units (what the assumptions table
 * shows) and converted with `scale`, so the table never has to know about the
 * engine's internal units and the engine never has to parse a formatted string.
 *
 * e.g. cost per readmission: base 14.6, unit 'K', scale 1000 → $14,600
 */
export interface RoiDriver {
  id: string;
  name: string;
  description?: string;
  /** Group heading in the assumptions table. */
  group: string;
  /** Suffix rendered after the value ('%', 'K', 'M', 'mo', ''). */
  unit: string;
  /** Display value × scale = engine value (dollars, months, or a 0–1 rate). */
  scale: number;
  role: DriverRole;
  conservative: number;
  base: number;
  aggressive: number;
  custom: number;
  /**
   * Uncertainty band, used by the tornado and as the denominator of the
   * confidence rule's uplift measure. Defaults to [conservative, aggressive].
   *
   * Set these EXPLICITLY for a driver that is canon across all three scenarios
   * but still carries measurement uncertainty — observed volumes, benchmarked
   * unit costs, and the approved budget. A scenario is a story about how well
   * the intervention performs and how fast it is adopted; it is not a licence to
   * restate how many patients the hospital has or what the CFO signed off on.
   * Without this split, "conservative" would quietly shrink the population and
   * the budget at the same time, which double-counts caution.
   */
  bandLow?: number;
  bandHigh?: number;
  /**
   * Sign of ∂(net 3-year value)/∂(driver). +1 where more is better (volume,
   * benefit rate), −1 where less is better (cost, ramp months). Used by the
   * confidence rule to tell an optimistic setting from a cautious one, and by
   * the tornado to label which end is upside.
   */
  direction: 1 | -1;
  /** Confidence in the SOURCE of this input, not in the outcome. */
  confidence: ConfidenceLevel;
  source: string;
  sourceType: SourceType;
  /** One line on why this driver moves the answer — shown in the tornado. */
  explanation?: string;
}

export interface RoiConfig {
  /** Annual discount rate applied to benefits. */
  discountRate: number;
  /** Years in the net-value horizon. */
  horizonYears: number;
  /** Published annual range = midpoint ± this fraction. */
  valueRangeSpread: number;
  /** Source confidence → a number, for the input-weighted confidence rule. */
  confidenceWeights: Record<ConfidenceLevel, number>;
  /**
   * How much an input set at the optimistic end of its band discounts its own
   * source confidence. Applied pro-rata to the distance travelled.
   */
  upsideConfidenceDiscount: number;
  /** Lower bounds (percent) for the High and Medium confidence labels. */
  confidenceBands: { high: number; medium: number };
  /** Payback search ceiling, in months. Beyond this, payback is `null`. */
  maxPaybackMonths: number;
}

/** The four numbers the financial model actually runs on. */
export interface ResolvedDrivers {
  annualValueMid: number;
  rampMonths: number;
  implementationCost: number;
  operatingCostPerYear: number;
  /** The multiplier chain's contribution. Kept so the on-screen value-driver
   *  bullets can quote their own share instead of restating a hand-typed figure. */
  multiplierValue: number;
  /** The additive drivers' combined contribution. */
  addendValue: number;
}

export interface RoiFinancials extends ResolvedDrivers {
  annualValueLow: number;
  annualValueHigh: number;
  year1RampFraction: number;
  year1RealizedValue: number;
  year1Cost: number;
  year1NetValue: number;
  totalCost3Yr: number;
  discountedValue3Yr: number;
  netValue3Yr: number;
  /** 3-year ROI: netValue3Yr / totalCost3Yr. Stated in the disclosure. */
  roi3YrPct: number;
  /** THE DISPLAYED FIGURE: year-1 net value / year-1 cost, ramp-adjusted. */
  year1RoiPct: number;
  returnMultiple: number;
  paybackMonths: number | null;
}

export interface RoiScenarioOutputs extends RoiFinancials {
  scenario: ScenarioId;
  confidencePct: number;
  confidenceLabel: ConfidenceLevel;
  /** Drivers whose custom value differs from base. Zero for named scenarios. */
  modifiedCount: number;
}

export interface SensitivityRow {
  rank: number;
  driverId: string;
  name: string;
  /** The base value with its unit, e.g. '10%' — what the row is anchored at. */
  baseLabel: string;
  /** Driver display values at the two band ends. */
  lowValue: number;
  highValue: number;
  /** Change in net 3-year value, in dollars. downside ≤ 0 ≤ upside. */
  downsideDelta: number;
  upsideDelta: number;
  /** upsideDelta − downsideDelta. The ranking key. */
  span: number;
  confidence: ConfidenceLevel;
  explanation: string;
}

// ============================================================================
// DRIVER RESOLUTION
// ============================================================================

/** The display-unit value a driver takes in a scenario. */
export function driverValue(driver: RoiDriver, scenario: ScenarioId): number {
  if (scenario === 'custom') return driver.custom;
  return driver[scenario];
}

/** Lower end of the uncertainty band. */
export function bandLowOf(driver: RoiDriver): number {
  return driver.bandLow ?? Math.min(driver.conservative, driver.aggressive);
}

/** Upper end of the uncertainty band. */
export function bandHighOf(driver: RoiDriver): number {
  return driver.bandHigh ?? Math.max(driver.conservative, driver.aggressive);
}

/**
 * True where the three named scenarios actually take different values — i.e.
 * this driver is a scenario lever rather than a canon input. Drives the "canon"
 * marker in the assumptions table so the identical Cons/Base/Agg columns on an
 * observed input read as deliberate rather than as missing data.
 */
export function isScenarioVaried(driver: RoiDriver): boolean {
  return driver.conservative !== driver.aggressive || driver.conservative !== driver.base;
}

/**
 * Collapse the driver set into the four numbers the financial model runs on.
 * `override` swaps a single driver's display value — this is how the tornado
 * perturbs one input at a time without cloning the whole set.
 */
export function resolveDrivers(
  drivers: readonly RoiDriver[],
  scenario: ScenarioId,
  override?: { driverId: string; value: number },
): ResolvedDrivers {
  let product = 1;
  let sawMultiplier = false;
  let addends = 0;
  let rampMonths = 1;
  let implementationCost = 0;
  let operatingCostPerYear = 0;

  for (const driver of drivers) {
    const display =
      override && override.driverId === driver.id ? override.value : driverValue(driver, scenario);
    const value = display * driver.scale;

    switch (driver.role) {
      case 'valueMultiplier':
        product *= value;
        sawMultiplier = true;
        break;
      case 'valueAddend':
        addends += value;
        break;
      case 'ramp':
        rampMonths = value;
        break;
      case 'implementationCost':
        implementationCost += value;
        break;
      case 'operatingCost':
        operatingCostPerYear += value;
        break;
    }
  }

  // A driver set with no multipliers has no multiplicative value chain; the
  // product must not contribute its identity element as $1 of benefit.
  const multiplierValue = sawMultiplier ? product : 0;

  return {
    annualValueMid: multiplierValue + addends,
    rampMonths,
    implementationCost,
    operatingCostPerYear,
    multiplierValue,
    addendValue: addends,
  };
}

// ============================================================================
// RAMP · PAYBACK · DISCOUNTING
// ============================================================================

/**
 * Mean fraction of the full run-rate realized over the first `throughMonth`
 * months, given a linear ramp to full over `rampMonths`.
 *
 * rampFraction(4) = mean(0.25, 0.5, 0.75, 1, 1×8) = 10.5/12 = 0.875
 */
export function rampFraction(rampMonths: number, throughMonth = 12): number {
  const ramp = Math.max(1, rampMonths);
  let sum = 0;
  for (let month = 1; month <= throughMonth; month += 1) {
    sum += Math.min(month / ramp, 1);
  }
  return sum / throughMonth;
}

/**
 * First month where cumulative ramped value covers cumulative cost.
 * Implementation lands at month 0; operating cost accrues monthly. Undiscounted.
 */
export function paybackMonths(
  drivers: ResolvedDrivers,
  maxMonths: number,
): number | null {
  const { annualValueMid, rampMonths, implementationCost, operatingCostPerYear } = drivers;
  if (annualValueMid <= 0) return null;

  const monthlyFullValue = annualValueMid / 12;
  const monthlyOperating = operatingCostPerYear / 12;
  const ramp = Math.max(1, rampMonths);

  let cumulativeValue = 0;
  for (let month = 1; month <= maxMonths; month += 1) {
    cumulativeValue += monthlyFullValue * Math.min(month / ramp, 1);
    const cumulativeCost = implementationCost + monthlyOperating * month;
    if (cumulativeValue >= cumulativeCost) return month;
  }
  return null;
}

/** Benefits over the horizon, year-end discounted. Year 1 is ramp-adjusted. */
export function discountedValue(drivers: ResolvedDrivers, config: RoiConfig): number {
  const { annualValueMid, rampMonths } = drivers;
  let total = 0;
  for (let year = 1; year <= config.horizonYears; year += 1) {
    const yearValue = year === 1 ? annualValueMid * rampFraction(rampMonths, 12) : annualValueMid;
    total += yearValue / Math.pow(1 + config.discountRate, year);
  }
  return total;
}

// ============================================================================
// FINANCIALS
// ============================================================================

export function computeFinancials(drivers: ResolvedDrivers, config: RoiConfig): RoiFinancials {
  const { annualValueMid, rampMonths, implementationCost, operatingCostPerYear } = drivers;

  const year1RampFraction = rampFraction(rampMonths, 12);
  const year1RealizedValue = annualValueMid * year1RampFraction;
  const year1Cost = implementationCost + operatingCostPerYear;

  const totalCost3Yr = implementationCost + operatingCostPerYear * config.horizonYears;
  const discountedValue3Yr = discountedValue(drivers, config);
  const netValue3Yr = discountedValue3Yr - totalCost3Yr;
  const year1NetValue = year1RealizedValue - year1Cost;

  return {
    ...drivers,
    annualValueLow: annualValueMid * (1 - config.valueRangeSpread),
    annualValueHigh: annualValueMid * (1 + config.valueRangeSpread),
    year1RampFraction,
    year1RealizedValue,
    year1Cost,
    year1NetValue,
    totalCost3Yr,
    discountedValue3Yr,
    netValue3Yr,
    roi3YrPct: totalCost3Yr > 0 ? (netValue3Yr / totalCost3Yr) * 100 : 0,
    // Same period on both sides of the ratio. See definition (5) in the header.
    year1RoiPct: year1Cost > 0 ? (year1NetValue / year1Cost) * 100 : 0,
    returnMultiple: totalCost3Yr > 0 ? discountedValue3Yr / totalCost3Yr : 0,
    paybackMonths: paybackMonths(drivers, config.maxPaybackMonths),
  };
}

// ============================================================================
// SENSITIVITY — the tornado, and the weights the confidence rule runs on
// ============================================================================

/**
 * Each driver swung across its own conservative↔aggressive band (NOT a uniform
 * ±20%, which would rank every multiplicative driver identically), measured as
 * the change in net 3-year value against the base case. Ranked by total span.
 *
 * Deliberately always evaluated at BASE. The sensitivity of the model is a
 * property of the model, published once — not something that re-ranks itself
 * depending on which scenario card is selected.
 */
export function computeSensitivity(
  drivers: readonly RoiDriver[],
  config: RoiConfig,
): SensitivityRow[] {
  const baseNet = computeFinancials(resolveDrivers(drivers, 'base'), config).netValue3Yr;

  const rows = drivers
    .map((driver) => {
      const netAt = (value: number) =>
        computeFinancials(
          resolveDrivers(drivers, 'base', { driverId: driver.id, value }),
          config,
        ).netValue3Yr;

      const low = bandLowOf(driver);
      const high = bandHighOf(driver);
      const deltaAtLow = netAt(low) - baseNet;
      const deltaAtHigh = netAt(high) - baseNet;

      const downsideDelta = Math.min(deltaAtLow, deltaAtHigh, 0);
      const upsideDelta = Math.max(deltaAtLow, deltaAtHigh, 0);

      return {
        rank: 0,
        driverId: driver.id,
        name: driver.name,
        baseLabel: `${driver.base}${driver.unit}`,
        lowValue: low,
        highValue: high,
        downsideDelta,
        upsideDelta,
        span: upsideDelta - downsideDelta,
        confidence: driver.confidence,
        explanation: driver.explanation ?? '',
      };
    })
    // Ties broken on driver id so the ranking is stable across renders.
    .sort((a, b) => b.span - a.span || a.driverId.localeCompare(b.driverId));

  return rows.map((row, index) => ({ ...row, rank: index + 1 }));
}

// ============================================================================
// CONFIDENCE — one number, one rule
// ============================================================================

/**
 * How far a driver has been pushed toward its optimistic end, 0..1, measured
 * against its uncertainty band. `direction` is what makes "optimistic"
 * well-defined for cost and ramp drivers, where the optimistic end is the LOWER
 * number.
 *
 * Measured against the BAND rather than the scenario spread so that a custom
 * edit to a canon input — pushing avoided cost per event to the top of its
 * benchmarked range, say — still registers as optimism. If this used the
 * scenario spread, canon inputs would have a zero-width denominator and could be
 * pushed anywhere for free.
 */
export function upliftFraction(driver: RoiDriver, scenario: ScenarioId): number {
  const optimisticEnd = driver.direction === 1 ? bandHighOf(driver) : bandLowOf(driver);
  const halfWidth = Math.abs(optimisticEnd - driver.base);
  if (halfWidth === 0) return 0;
  const uplift = driver.direction * (driverValue(driver, scenario) - driver.base);
  return Math.min(1, Math.max(0, uplift / halfWidth));
}

/**
 * THE STATED RULE. Confidence is the sensitivity-weighted mean of the inputs'
 * SOURCE confidence, discounted for inputs set above their benchmark value:
 *
 *   confidence = Σ( span_i × w(source_i) × (1 − k × uplift_i) ) / Σ( span_i )
 *
 * Three consequences worth being explicit about, because they are the reason
 * this is a rule and not a vibe:
 *   - Weighting by sensitivity span means a shaky input that barely moves the
 *     answer barely moves confidence. An unweighted mean would let a trivial
 *     estimate drag the number down as hard as the top driver.
 *   - Conservative scores the SAME as base: pulling an input below its
 *     benchmark is not an act of optimism, so it takes no discount.
 *   - Custom is scored by the identical rule, so custom-equals-base gives
 *     exactly the base number, and any optimistic edit visibly costs confidence.
 */
export function computeConfidence(
  drivers: readonly RoiDriver[],
  scenario: ScenarioId,
  config: RoiConfig,
  sensitivity: readonly SensitivityRow[],
): { pct: number; label: ConfidenceLevel } {
  const spanById = new Map(sensitivity.map((row) => [row.driverId, row.span]));

  let weighted = 0;
  let totalSpan = 0;

  for (const driver of drivers) {
    const span = spanById.get(driver.id) ?? 0;
    if (span <= 0) continue;
    const sourceWeight = config.confidenceWeights[driver.confidence];
    const discount = 1 - config.upsideConfidenceDiscount * upliftFraction(driver, scenario);
    weighted += span * sourceWeight * discount;
    totalSpan += span;
  }

  const pct = totalSpan > 0 ? Math.round((weighted / totalSpan) * 100) : 0;
  return { pct, label: confidenceLabel(pct, config) };
}

export function confidenceLabel(pct: number, config: RoiConfig): ConfidenceLevel {
  if (pct >= config.confidenceBands.high) return 'High';
  if (pct >= config.confidenceBands.medium) return 'Medium';
  return 'Low';
}

// ============================================================================
// TOP-LEVEL ENTRY POINTS
// ============================================================================

/**
 * Has this input actually been moved off its base value?
 *
 * THE ONE PREDICATE. The "N Modified" badge, the row highlight, the orange dot and
 * the per-row reset button all ask this same question, and they used to ask it in
 * two different places — the badge through modifiedDriverIds(), the row through an
 * inline `custom !== base`. Two comparisons of one fact is how they drift apart.
 *
 * The tolerance matters for driver sets whose base is computed rather than typed
 * (buildGenericDrivers derives its midpoint by division). Base and custom start
 * from the same expression there, so they are bit-identical today — but a future
 * driver set that computes them via two equivalent routes could differ in the last
 * float bit and light up a badge nobody caused. A delta below 1e-9 is never a human
 * edit through a number input, so treating it as "unmodified" costs nothing and
 * removes a whole class of phantom badge.
 */
export function isModified(driver: RoiDriver): boolean {
  return Math.abs(driver.custom - driver.base) > 1e-9;
}

/** Drivers the user has actually moved off base. This IS the "N Modified" badge. */
export function modifiedDriverIds(drivers: readonly RoiDriver[]): string[] {
  return drivers.filter(isModified).map((driver) => driver.id);
}

export function computeScenario(
  drivers: readonly RoiDriver[],
  scenario: ScenarioId,
  config: RoiConfig,
  sensitivity?: readonly SensitivityRow[],
): RoiScenarioOutputs {
  const rows = sensitivity ?? computeSensitivity(drivers, config);
  const financials = computeFinancials(resolveDrivers(drivers, scenario), config);
  const confidence = computeConfidence(drivers, scenario, config, rows);

  return {
    ...financials,
    scenario,
    confidencePct: confidence.pct,
    confidenceLabel: confidence.label,
    modifiedCount: scenario === 'custom' ? modifiedDriverIds(drivers).length : 0,
  };
}

export interface RoiModel {
  drivers: readonly RoiDriver[];
  config: RoiConfig;
  sensitivity: SensitivityRow[];
  scenarios: Record<ScenarioId, RoiScenarioOutputs>;
  /** Conservative low → aggressive high. The span the PDFs quote. */
  annualValueRangeLow: number;
  annualValueRangeHigh: number;
}

/**
 * Compute everything once. Sensitivity is computed a single time and threaded
 * into all four scenarios, so the tornado and the confidence weights can never
 * disagree with each other.
 */
export function computeRoiModel(drivers: readonly RoiDriver[], config: RoiConfig): RoiModel {
  const sensitivity = computeSensitivity(drivers, config);
  const scenarios = {
    conservative: computeScenario(drivers, 'conservative', config, sensitivity),
    base: computeScenario(drivers, 'base', config, sensitivity),
    aggressive: computeScenario(drivers, 'aggressive', config, sensitivity),
    custom: computeScenario(drivers, 'custom', config, sensitivity),
  };

  return {
    drivers,
    config,
    sensitivity,
    scenarios,
    annualValueRangeLow: scenarios.conservative.annualValueLow,
    annualValueRangeHigh: scenarios.aggressive.annualValueHigh,
  };
}

// ============================================================================
// FORMATTING — colocated so two surfaces cannot round the same figure differently
// ============================================================================

/** $16.9M / $850K / $0. One decimal at $M, whole numbers at $K. */
export function formatUsd(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
  return `${sign}$${Math.round(abs)}`;
}

/** '$15.2M – $18.6M'. En dash, spaced — the artifact house style. */
export function formatUsdRange(low: number, high: number): string {
  return `${formatUsd(low)} – ${formatUsd(high)}`;
}

/**
 * A driver's display value with its unit attached — '$14.6K', '12%', '4 mo',
 * '85,000'. Shared so the assumptions table and the CFO panel cannot render the
 * same input two different ways.
 */
export function formatDriverValue(driver: RoiDriver, value: number): string {
  switch (driver.unit) {
    case 'M':
    case 'K':
      return `$${value}${driver.unit}`;
    case '%':
      return `${value}%`;
    case 'mo':
      return `${value} mo`;
    case '':
      return value.toLocaleString('en-US');
    default:
      return `${value}${driver.unit}`;
  }
}

/** '19.8×' — the return multiple. */
export function formatMultiple(multiple: number): string {
  return `${multiple.toFixed(1)}×`;
}

/** '885%' — an ROI percentage. Always render it with its period label. */
export function formatRoiPct(roiPct: number): string {
  return `${Math.round(roiPct)}%`;
}

export function formatMonths(months: number | null): string {
  if (months === null) return 'Not recovered in horizon';
  return `${months} ${months === 1 ? 'month' : 'months'}`;
}
